import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Facebook } from 'lucide-react';
import { contactInfo } from '../data/mock';

const Header = () => {
  const FACEBOOK_ICON_SIZE = 22;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // setIsScrolled is stable from useState, no dependency needed

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/financing', label: 'Financing' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-navy-dark/95 backdrop-blur-md shadow-xl' : 'bg-navy-dark'
    }`}>
      {/* Top bar */}
      <div className="bg-navy-darker border-b border-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 text-sm">
            <div className="text-gray-300">
              <span className="hidden sm:inline">Excellence Meets Affordability® | </span>
              <span>Over 10 Years Serving Colorado</span>
            </div>
            <a 
              href={`tel:${contactInfo.phoneRaw}`}
              className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors"
            >
              <Phone size={16} />
              <span className="font-semibold">{contactInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_e4501b70-3916-4f5c-9cb2-b91d80cef241/artifacts/ywaw69v6_Main%20Circle%20Cut.png"
              alt="Savior Windows Logo"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105 brightness-110"
              style={{ mixBlendMode: 'lighten' }}
            />
            <div className="hidden lg:block">
              <div className="text-white font-bold text-lg tracking-wide leading-tight">
                Savior Replacement Windows and Doors, LLC
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="https://www.facebook.com/profile.php?id=61586173917757"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:text-gold-light transition-colors transform hover:scale-110 duration-200"
              aria-label="Visit our Facebook page"
            >
              <Facebook size={FACEBOOK_ICON_SIZE} />
            </a>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-all duration-200 relative group ${
                  location.pathname === link.path
                    ? 'text-gold'
                    : 'text-gray-200 hover:text-gold'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-gold transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                }`}></span>
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-gold text-navy-dark px-6 py-2.5 rounded-md font-semibold hover:bg-gold-light transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-gold/50"
            >
              Free Quote
            </Link>
          </nav>

          {/* Mobile menu button and Facebook icon */}
          <div className="md:hidden flex items-center gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61586173917757"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:text-gold-light transition-colors transform hover:scale-110 duration-200"
              aria-label="Visit our Facebook page"
            >
              <Facebook size={24} />
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white p-2 hover:text-gold transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-navy-darker border-t border-gold/20 animate-slideDown">
          <nav className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=61586173917757"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-lg font-medium py-2 px-4 rounded transition-colors text-gray-200 hover:text-gold hover:bg-gold/5 flex items-center gap-2"
            >
              <Facebook size={20} />
              <span>Follow Us on Facebook</span>
            </a>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-lg font-medium py-2 px-4 rounded transition-colors ${
                  location.pathname === link.path
                    ? 'text-gold bg-gold/10'
                    : 'text-gray-200 hover:text-gold hover:bg-gold/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-gold text-navy-dark px-6 py-3 rounded-md font-semibold text-center hover:bg-gold-light transition-colors mt-2"
            >
              Free Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
